import React, { useState, useEffect } from 'react';
import { User, Calendar, Tag, DollarSign, Zap, House, Building2, Columns, Calculator, FileText, Loader2, Eye, FilePlus2, RefreshCw, Repeat, FileCheck2, ShieldAlert } from 'lucide-react';
import { Negociacao, UnidadeConsumidora, Profile, Pipeline, PipelineStage } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import TransferUserModal from './TransferUserModal';
import TrocaTitularidadeModal, { ModoTrocaTitularidade } from './TrocaTitularidadeModal';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import SimulationModal, { SimulacaoTermo, CobrancaDoTermo } from './SimulationModal';
import SimulationHistoryModal from './SimulationHistoryModal';
import TermHistoryModal from './TermHistoryModal';
import { useToast } from '../../../../src/contexts/ToastContext';
import SlaTimer from '../../../../src/components/SlaTimer';
import ConfirmModal from '../../../../src/components/ConfirmModal';
import { fetchSignatariosTermo, SignatariosTermo } from '../../../../src/lib/termoSignatarios';
import { getEnderecoTitularLemit, EnderecoTitular } from '../../../../src/services/lemit';
import { validarCPF, validarCNPJ, sanitizeNomeParaArquivo } from '../../../../src/lib/utils';
import AvalistaRecoveryPanel from './AvalistaRecoveryPanel';
import DeclaracaoNaoAssociacaoPanel from './DeclaracaoNaoAssociacaoPanel';
import { useDeclaracoesNaoAssociacao } from '../../../../src/hooks/useDeclaracoesNaoAssociacao';
import { ORIGENS_NEGOCIACAO } from '../../../../src/lib/origensNegociacao';
import { podeTransferirNegociacao, transferenciaRestritaAEquipe } from '../../../../src/lib/transferenciaNegociacao';
import { ehDaEquipeSupervisionada } from '../../../../src/lib/supervisaoComercial';
import { nomeEtapaNaTela } from '../../../../src/lib/nomeEtapaNaTela';

interface DetailLeftColumnProps {
    negotiation: Negociacao;
    ucs: UnidadeConsumidora[];
    onUpdate: () => void;
    onClose: () => void;
}

const DetailLeftColumn: React.FC<DetailLeftColumnProps> = ({ negotiation, ucs, onUpdate, onClose }) => {
    const { user } = useAuth();
    const { addToast } = useToast();

    // Metadata State
    const [pipelines, setPipelines] = useState<Pipeline[]>([]);
    const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);

    const [formData, setFormData] = useState({
        pipeline_id: negotiation.pipeline_id || '',
        stage: negotiation.stage,
        cliente_nome: negotiation.cliente_nome,
        consumo_medio_kwh: negotiation.consumo_medio_kwh,
        created_at: negotiation.created_at.split('T')[0],
        previsao_fechamento: negotiation.previsao_fechamento ? negotiation.previsao_fechamento.split('T')[0] : '',
        origem: negotiation.origem || 'Outros',
        plataforma: negotiation.plataforma || '',
        campanha: negotiation.campanha || '',
        anuncio: negotiation.anuncio || '',
        formulario: negotiation.formulario || '',
        reside_goias: negotiation.reside_goias === true ? 'sim' : negotiation.reside_goias === false ? 'nao' : '',
    });

    const [showTransferModal, setShowTransferModal] = useState(false);
    const [podeTransferir, setPodeTransferir] = useState(false);
    // Quem só supervisiona o Comercial transfere entre Closers, BDRs e ele mesmo (#6521).
    const [transferenciaSoNaEquipe, setTransferenciaSoNaEquipe] = useState(false);

    // Action States
    const [loadingAction, setLoadingAction] = useState<string | null>(null);

    // analise_cadastral vem como array do embed do NegotiationDetailModal
    // (`analise_cadastral(decisao_final)`); há no máximo 1 linha por negociação
    // (unique de 20260709120000_dedup_analise_cadastral_unique.sql).
    const decisaoFinalAnalise: string | undefined = negotiation.analise_cadastral?.[0]?.decisao_final ?? undefined;
    const creditoReprovado = decisaoFinalAnalise === 'Reprovado';
    // Declaração de não associação emitida = negociação encerrada: some o avalista e o
    // "Reenviar para análise". O banco recusa os dois de qualquer jeito (migration
    // 20261016120000); esconder aqui evita o usuário bater no erro.
    const { declaracoes: declaracoesNaoAssociacao, recarregar: recarregarDeclaracoes } =
        useDeclaracoesNaoAssociacao(negotiation.id, negotiation.status === 'termo_reprovado');
    const negociacaoEncerrada = declaracoesNaoAssociacao.length > 0;
    const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [isTermModalOpen, setIsTermModalOpen] = useState(false);
    const [showTermHistoryModal, setShowTermHistoryModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showSignedTermWarning, setShowSignedTermWarning] = useState(false);
    const [showTrocaTitularidadeModal, setShowTrocaTitularidadeModal] = useState(false);
    const [modoTrocaTitularidade, setModoTrocaTitularidade] = useState<ModoTrocaTitularidade>('enviar');
    const [showSignedTrocaTitularidadeWarning, setShowSignedTrocaTitularidadeWarning] = useState(false);
    // UCs da negociação que já têm Declaração de Troca de Titularidade gerada. A troca
    // é por UC: o cliente pode assinar a de uma hoje e a da outra depois, então a tela
    // precisa saber se ainda sobra UC sem documento para oferecer o envio.
    const [ucsComTroca, setUcsComTroca] = useState<{ comDocumento: Set<number>; assinadas: Set<number> } | null>(null);
    // Reenvio sem mudar o status (documento_enviado → documento_enviado) não dispara a
    // releitura sozinho; fechar o modal soma 1 aqui para forçar.
    const [releituraTroca, setReleituraTroca] = useState(0);

    // Check if simulation/term was generated based on tags
    const simulationGenerated = negotiation.tags?.includes('Simulação Gerada') || false;
    const termGenerated = negotiation.tags?.includes('Termo Gerado') || false;


    useEffect(() => {
        const fetchMetadata = async () => {
            const { data: pData } = await supabase.from('pipelines').select('*').order('created_at');
            const { data: sData } = await supabase.from('pipeline_stages').select('*').order('position');
            if (pData) setPipelines(pData);
            if (sData) setPipelineStages(sData);
        };
        fetchMetadata();
    }, []);

    // Lido do banco a cada abertura (e não do perfil do AuthContext) para que uma
    // permissão recém-concedida valha sem a pessoa precisar sair e entrar de novo.
    useEffect(() => {
        const fetchPermissaoTransferir = async () => {
            if (user) {
                const { data } = await supabase.from('profiles').select('role, status, permissions').eq('id', user.id).single();
                setPodeTransferir(podeTransferirNegociacao(data));
                setTransferenciaSoNaEquipe(transferenciaRestritaAEquipe(data));
            }
        };
        fetchPermissaoTransferir();
    }, [user]);

    const trocaJaEnviada = negotiation.troca_titularidade_status === 'documento_enviado'
        || negotiation.troca_titularidade_status === 'aguardando_reanalise';
    const idsUcs = ucs.map(u => u.id).join(',');
    useEffect(() => {
        if (!trocaJaEnviada || ucs.length < 2) {
            setUcsComTroca(null);
            return;
        }
        let cancelado = false;
        (async () => {
            const { data } = await supabase
                .from('termo_troca_titularidade_logs')
                .select('uc_id, assinado_em')
                .eq('negociacao_id', negotiation.id);
            if (cancelado || !data) return;
            const comDocumento = new Set<number>();
            const assinadas = new Set<number>();
            for (const log of data) {
                if (log.uc_id == null) continue;
                comDocumento.add(log.uc_id);
                if (log.assinado_em) assinadas.add(log.uc_id);
            }
            setUcsComTroca({ comDocumento, assinadas });
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [negotiation.id, negotiation.troca_titularidade_status, trocaJaEnviada, idsUcs, releituraTroca]);

    const ucsSemTroca = ucsComTroca ? ucs.filter(u => !ucsComTroca.comDocumento.has(u.id)).length : 0;
    const ucsTrocaAssinada = ucsComTroca ? ucs.filter(u => ucsComTroca.assinadas.has(u.id)).length : 0;

    const abrirTrocaTitularidade = (modo: ModoTrocaTitularidade) => {
        setModoTrocaTitularidade(modo);
        setShowTrocaTitularidadeModal(true);
    };

    const botaoTrocaOutraUc = (
        <button
            onClick={() => abrirTrocaTitularidade('outraUc')}
            className="w-full py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20"
            title="Gera a Declaração de Troca de Titularidade das UCs desta negociação que ainda não têm documento. As já enviadas não mudam."
        >
            <FilePlus2 size={16} />
            {ucsSemTroca === 1 ? 'Enviar Troca de Outra UC' : `Enviar Troca de Outra UC (${ucsSemTroca} sem documento)`}
        </button>
    );

    // Calc total consumption from UCs if needed, or use stored
    useEffect(() => {
        setFormData({
            pipeline_id: negotiation.pipeline_id || (pipelines.length > 0 ? pipelines[0].id : ''),
            stage: negotiation.stage,
            cliente_nome: negotiation.cliente_nome,
            consumo_medio_kwh: negotiation.consumo_medio_kwh,
            created_at: negotiation.created_at.split('T')[0],
            previsao_fechamento: negotiation.previsao_fechamento ? negotiation.previsao_fechamento.split('T')[0] : '',
            origem: negotiation.origem || 'Outros',
            plataforma: negotiation.plataforma || '',
            campanha: negotiation.campanha || '',
            anuncio: negotiation.anuncio || '',
            formulario: negotiation.formulario || '',
            reside_goias: negotiation.reside_goias === true ? 'sim' : negotiation.reside_goias === false ? 'nao' : '',
        });
    }, [negotiation, pipelines]);

    const handlePipelineChange = async (newPipelineId: string) => {
        if (!newPipelineId || newPipelineId === formData.pipeline_id) return;

        // Find default stage (first) for new pipeline
        const defaultStage = pipelineStages
            .filter(s => s.pipeline_id === newPipelineId)
            .sort((a, b) => a.position - b.position)[0]?.name || '';

        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({ pipeline_id: newPipelineId, stage: defaultStage })
                .eq('id', negotiation.id);

            if (error) throw error;

            setFormData({ ...formData, pipeline_id: newPipelineId, stage: defaultStage });

            // Log
            const oldPipeline = pipelines.find(p => p.id === formData.pipeline_id)?.name || 'Anterior';
            const newPipeline = pipelines.find(p => p.id === newPipelineId)?.name || 'Novo';
            await logNegotiationAction(negotiation.id, user?.id, `moveu do funil "${oldPipeline}" para "${newPipeline}"`, { from: formData.pipeline_id, to: newPipelineId });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_pipeline_negociacao', origem: 'acao' });

            onUpdate();
        } catch (error: any) {
            console.error('Error updating pipeline:', error);
            addToast('Erro ao mover negociação de funil: ' + (error.message || 'Erro desconhecido'), 'error');
        }
    };

    const handleBlur = async (field: string, value: any) => {
        if (value === (negotiation as any)[field]) return; // No change

        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({ [field]: value })
                .eq('id', negotiation.id);

            if (error) throw error;

            // Logging
            if (field === 'stage') {
                await logNegotiationAction(negotiation.id, user?.id, `moveu para etapa ${value.replace('_', ' ')}`, { from: negotiation.stage, to: value });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_etapa_detalhe', origem: 'acao' });
            } else if (field === 'origem') {
                await logNegotiationAction(negotiation.id, user?.id, `alterou a origem para ${value}`, { from: negotiation.origem, to: value });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_origem_negociacao', origem: 'acao' });
            }

            onUpdate();
        } catch (error) {
            console.error('Error updating field:', error);
        }
    };

    const handleDelete = async () => {
        try {
            // 1. Check Permissions
            if (!user) return;

            const { data: profile } = await supabase
                .from('profiles')
                .select('role')
                .eq('id', user.id)
                .single();

            if (profile?.role === 'vendedor' || profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro') {
                // Notifica os gerentes sobre a tentativa
                const rolesToNotify = ['gerente'];
                if (profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro') {
                    rolesToNotify.push('gerente_parcerias');
                }

                const { data: managers } = await supabase
                    .from('profiles')
                    .select('id')
                    .in('role', rolesToNotify);

                if (managers && managers.length > 0) {
                    for (const m of managers) {
                        const notificacao = {
                            user_id: m.id,
                            type: 'negotiation_assigned',
                            title: 'Tentativa de Exclusão Bloqueada',
                            content: `O usuário ${user.user_metadata?.full_name || 'Desconhecido'} (${profile.role}) tentou excluir a negociação "${negotiation.titulo || negotiation.cliente_nome}".`,
                            link: `/crm?negociacaoId=${negotiation.id}`,
                            read: false
                        };
                        const { error: notifError } = await supabase.from('notifications').insert(notificacao);
                        if (notifError) console.error(`Erro ao inserir notificação para gerente ${m.id}:`, notifError);
                    }

                    // Inserir nota de solicitação de exclusão para o feed
                    await supabase.from('negociacao_notas').insert({
                        negociacao_id: negotiation.id,
                        user_id: user.id,
                        conteudo: `[DELETE_REQUEST] O usuário ${user.user_metadata?.full_name || 'Desconhecido'} solicitou a exclusão desta negociação. Motivo: Bloqueio de permissão.`,
                        tipo: 'nota'
                    });
                }

                if (profile?.role === 'vendedor') {
                    addToast('Permissão negada: Você não tem permissão para excluir negociações. O Gerente Comercial foi notificado.', 'error');
                } else {
                    addToast('Permissão negada: Apenas o Gerente de Parcerias ou Comercial podem excluir negociações. Os gerentes foram notificados.', 'error');
                }
                return;
            }

            // 2. Check for Pending Tasks
            const { data: activeTasks, error: taskError } = await supabase
                .from('tarefas')
                .select('id')
                .eq('negociacao_id', negotiation.id)
                .in('status', ['a_iniciar', 'em_andamento', 'aguardando']);

            if (taskError) throw taskError;

            if (activeTasks && activeTasks.length > 0) {
                addToast('Existem tarefas pendentes para essa negociação. Finalize-as e tente novamente.', 'warning');
                return;
            }

            // 3. Open confirmation modal instead of native confirm()
            setShowDeleteConfirm(true);
        } catch (error) {
            console.error('Error checking delete permissions:', error);
            addToast('Erro ao verificar permissões.', 'error');
        }
    };

    const executeDelete = async () => {
        try {

            // 4. Cleanup & Delete (order matters: child tables first, then parent)
            // Note: UCs are auto-deleted via ON DELETE CASCADE on negociacao_id FK

            // Delete task comments first (FK to tarefas)
            const { data: relatedTasks } = await supabase
                .from('tarefas')
                .select('id')
                .eq('negociacao_id', negotiation.id);

            if (relatedTasks && relatedTasks.length > 0) {
                const taskIds = relatedTasks.map(t => t.id);
                await supabase
                    .from('comentarios_tarefas')
                    .delete()
                    .in('tarefa_id', taskIds);
            }

            // Delete ALL tasks associated
            await supabase
                .from('tarefas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete negotiation notes (FK to negociacoes)
            await supabase
                .from('negociacao_notas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete negotiation logs (FK to negociacoes)
            await supabase
                .from('negociacao_logs')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete analise cadastral (FK to negociacoes)
            await supabase
                .from('analise_cadastral')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete audit records (FK to negociacoes)
            await supabase
                .from('negociacoes_documentos_audit')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete comissions (FK to negociacoes)
            await supabase
                .from('comissao_parceiros')
                .delete()
                .eq('negociacao_id', negotiation.id);

            await supabase
                .from('comissao_vendedor_negociacoes')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete pre-venda records (FK to negociacoes)
            await supabase
                .from('pre_venda')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete whatsapp conversations (FK to negociacoes)
            await supabase
                .from('whatsapp_conversas')
                .delete()
                .eq('negociacao_id', negotiation.id);

            // Delete Negotiation
            const { error } = await supabase
                .from('negociacoes')
                .delete()
                .eq('id', negotiation.id);

            if (error) throw error;

            setShowDeleteConfirm(false);
            onUpdate();
            onClose();

        } catch (error) {
            console.error('Error deleting negotiation:', error);
            addToast('Erro ao excluir negociação.', 'error');
            setShowDeleteConfirm(false);
        }
    };

    // Calculate days delayed
    const getDelayText = () => {
        if (!formData.previsao_fechamento) return null;
        const today = new Date();
        const forecast = new Date(formData.previsao_fechamento);
        const diffTime = forecast.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return <span className="text-xs text-orange-500 font-medium mt-1 block">O negócio está atrasado em {Math.abs(diffDays)} dia(s)</span>;
        }
        return null;
    };

    const handleTransfer = async (targetUser: Profile) => {
        try {
            // Sem checar o erro, uma falha no banco fechava o modal como se a
            // transferência tivesse dado certo e ainda gravava o log dela.
            const { error } = await supabase
                .from('negociacoes')
                .update({ responsavel_id: targetUser.id })
                .eq('id', negotiation.id);
            if (error) throw error;

            await logNegotiationAction(negotiation.id, user?.id, `alterou o responsável para ${targetUser.full_name}`, { previous: negotiation.responsavel_id, new: targetUser.id });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'transferir_negociacao', origem: 'acao' });

            onUpdate();
            setShowTransferModal(false);
        } catch (error) {
            console.error('Error transferring negotiation:', error);
            addToast('Erro ao transferir negociação.', 'error');
        }
    };

    const handleSimulateEconomy = async () => {
        setIsSimulationModalOpen(true);
    };

    const handleGenerateContract = async (selectedUCsList: UnidadeConsumidora[] = [], enderecoTitularPrevio?: EnderecoTitular | null, simulacaoEscolhida?: SimulacaoTermo | null, cobranca?: CobrancaDoTermo) => {
        // --- VALIDATION START ---
        // A janela do termo grava WhatsApp, e-mail e financeiro logo antes (#6384):
        // valida com o cadastro de agora, não com o de quando a negociação abriu.
        const { data: cadastroAtual } = await supabase
            .from('negociacoes')
            .select('cliente_nome, whatsapp, cpf_cnpj, email, tipo_cliente, nome_representante_legal, cpf_representante')
            .eq('id', negotiation.id)
            .maybeSingle();
        const cad = { ...negotiation, ...(cadastroAtual || {}) } as typeof negotiation;
        const isPJ = cad.tipo_cliente === 'PJ';
        const missingNegFields: string[] = [];

        if (!cad.cliente_nome?.trim()) missingNegFields.push(isPJ ? 'Razão Social' : 'Nome do Cliente');
        if (!cad.whatsapp?.trim()) missingNegFields.push('WhatsApp');
        if (!cad.cpf_cnpj?.trim()) missingNegFields.push(isPJ ? 'CNPJ' : 'CPF');
        else if (!(isPJ ? validarCNPJ(cad.cpf_cnpj) : validarCPF(cad.cpf_cnpj))) missingNegFields.push(isPJ ? 'CNPJ (número inválido)' : 'CPF (número inválido)');
        if (!cad.email?.trim()) missingNegFields.push('E-mail');

        if (isPJ) {
            if (!cad.nome_representante_legal?.trim()) missingNegFields.push('Nome do Representante Legal');
            if (!cad.cpf_representante?.trim()) missingNegFields.push('CPF do Representante');
            else if (!validarCPF(cad.cpf_representante)) missingNegFields.push('CPF do Representante (número inválido — a Autentique rejeita o documento assinado se o CPF não for válido)');
        }

        if (missingNegFields.length > 0) {
            addToast('Dados da negociação incompletos: ' + missingNegFields.join(', ') + '. Corrija no lápis de Dados do Cliente e gere o termo de novo.', 'error');
            return false;
        }

        const ucsToValidate = selectedUCsList.length > 0 ? selectedUCsList : ucs;
        if (ucsToValidate.length === 0) {
            addToast('Nenhuma UC vinculada para gerar o termo.', 'error');
            return false;
        }

        const missingUcFieldsMap: { [uc: string]: string[] } = {};
        const requiredUcFields = {
            numero_uc: 'Número da UC',
            num_medidor: 'Nº do Medidor',
            concessionaria: 'Concessionária',
            consumo_medio_kwh: 'Consumo Médio',
            valor_tarifa: 'Valor Tarifa',
            tipo_ligacao: 'Tipo de Ligação',
            grupo_tarifario: 'Grupo Tarifário',
            titular_fatura: 'Nome do Titular',
            cpf_cnpj_titular: 'CPF/CNPJ do Titular',
            // Data de nascimento não vai para o termo (só análise cadastral) — não bloqueia a emissão.
            cep: 'CEP',
            logradouro: 'Logradouro',
            numero: 'Número',
            bairro: 'Bairro',
            cidade: 'Cidade',
            estado: 'Estado'
        };

        ucsToValidate.forEach(uc => {
            const missing = Object.entries(requiredUcFields)
                .filter(([key]) => {
                    const value = (uc as any)[key];
                    return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
                })
                .map(([_, label]) => label);

            if (missing.length > 0) {
                missingUcFieldsMap[uc.numero_uc] = missing;
            }
        });

        if (Object.keys(missingUcFieldsMap).length > 0) {
            const firstUcNum = Object.keys(missingUcFieldsMap)[0];
            addToast(`Dados da UC ${firstUcNum} incompletos: ${missingUcFieldsMap[firstUcNum].join(', ')}`, 'error');
            return false;
        }
        // --- VALIDATION END ---

        setLoadingAction('generate_contract');

        try {
            // 1. Busca configurações e dados atualizados da negociação
            const [{ data: settings }, { data: negotiationData }] = await Promise.all([
                supabase.from('system_settings').select('value').eq('key', 'issuance_of_terms_of_agreement').single(),
                supabase.from('negociacoes').select('*').eq('id', negotiation.id).single()
            ]);

            const webhookUrl = settings?.value || "https://n8n.srv1179066.hstgr.cloud/webhook/8d1cf955-73fb-46f2-bf3c-ed36b4fbfa51";

            if (!webhookUrl) {
                addToast('URL do webhook de geração de termo não configurada.', 'error');
                return false;
            }

            // O Quadro Resumo do termo (rev V) referencia número, data e link do Plano de Benefícios (simulação).
            // O usuário escolhe qual simulação anexar no modal do termo; fallback: a mais recente.
            const simulacoes = negotiationData?.url_simulacao || [];
            const simulacaoAnexada = simulacaoEscolhida ?? (simulacoes.length > 0 ? simulacoes[simulacoes.length - 1] : null);
            if (!simulacaoAnexada) {
                addToast('Gere a simulação de economia antes de emitir o termo — o Quadro Resumo referencia o Plano de Benefícios.', 'error');
                return false;
            }

            // Testemunhas e procurador configurados em Configurações → Assinaturas do Termo
            let signatarios: SignatariosTermo;
            try {
                signatarios = await fetchSignatariosTermo();
            } catch (sigError: any) {
                addToast(sigError.message, 'error');
                return false;
            }

            // 2. Busca o nome do vendedor (responsável) da tabela profiles
            let vendedorNome = '';
            if (negotiationData?.responsavel_id) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('full_name')
                    .eq('id', negotiationData.responsavel_id)
                    .single();
                vendedorNome = profileData?.full_name || '';
            }

            // Referência da principal UC selecionada (dados técnicos + fallback de endereço)
            const primeiraUC = (selectedUCsList[0] || ucs[0] || {}) as any;

            // Endereço do contratante = endereço cadastral do CPF/CNPJ (Lemit).
            // O modal do termo já consultou e mostrou o preview — reusa o resultado.
            // Se chamado sem preview (undefined), consulta aqui; sem Lemit, cai na 1ª UC.
            let enderecoTitular: EnderecoTitular | null = enderecoTitularPrevio ?? null;
            if (enderecoTitularPrevio === undefined) {
                try {
                    enderecoTitular = await getEnderecoTitularLemit(negotiationData?.cpf_cnpj || negotiation.cpf_cnpj || '', negotiation.id);
                } catch (lemitError: any) {
                    console.warn('Lemit endereço do titular:', lemitError?.message || lemitError);
                }
                if (!enderecoTitular) {
                    addToast('Endereço do titular não encontrado na Lemit — o termo sairá com o endereço da 1ª UC.', 'warning');
                }
            }

            // 3. Monta o payload base com os dados REAIS das tabelas
            const payload: any = {
                // Da tabela 'negociacoes'
                id: negotiationData?.id || '',
                cliente_nome: negotiationData?.cliente_nome || '',
                cliente_nome_arquivo: sanitizeNomeParaArquivo(negotiationData?.cliente_nome || ''),
                cpf_cnpj: negotiationData?.cpf_cnpj || '',
                email: negotiationData?.email || '',
                whatsapp: negotiationData?.whatsapp || '',
                tipo_cliente: negotiationData?.tipo_cliente || '',
                fidelidade: negotiationData?.fidelidade || '',
                desconto_adicional: negotiationData?.desconto_adicional || '',
                vendedor_nome: vendedorNome,

                // Endereço do contratante — escolhido no modal do termo (Lemit ou UC); fallback: 1ª UC.
                // Quadra/lote só existem quando a origem é o cadastro de uma UC; no endereço Lemit eles já vêm dentro do complemento.
                rua: enderecoTitular ? enderecoTitular.rua : (primeiraUC.logradouro || ''),
                numero: enderecoTitular ? enderecoTitular.numero : (primeiraUC.numero || ''),
                quadra: enderecoTitular ? (enderecoTitular.quadra || '') : (primeiraUC.quadra || ''),
                lote: enderecoTitular ? (enderecoTitular.lote || '') : (primeiraUC.lote || ''),
                bairro: enderecoTitular ? enderecoTitular.bairro : (primeiraUC.bairro || ''),
                cidade: enderecoTitular ? enderecoTitular.cidade : (primeiraUC.cidade || ''),
                estado: enderecoTitular ? enderecoTitular.estado : (primeiraUC.estado || ''),
                cep: enderecoTitular ? enderecoTitular.cep : (primeiraUC.cep || ''),
                complemento: enderecoTitular ? enderecoTitular.complemento : (primeiraUC.complemento || ''),

                // Dados técnicos da primeira UC
                nome_concessionaria: primeiraUC.concessionaria || '',
                desconto_tarifa: primeiraUC.desconto_tarifa || '',
                desconto_bandeira: primeiraUC.desconto_bandeira || '',

                // Metadados
                data_assinatura: new Date().toLocaleDateString('pt-BR'),
                negociacao_id: negotiation.id,

                // Representante Legal (PJ)
                cpf_representante: negotiationData?.cpf_representante || '',
                nome_representante_legal: negotiationData?.nome_representante_legal || '',
                representante_legal: negotiationData?.nome_representante_legal || '',
                nome_representante: negotiationData?.nome_representante_legal || '',

                // Financeiro (Quadro Resumo do termo). Vazio aqui não é problema: o Code node
                // do n8n troca cada campo vazio pelo dado principal do cliente antes de gerar o doc.
                contato_financeiro: negotiationData?.contato_financeiro || '',
                email_financeiro: negotiationData?.email_financeiro || '',
                whatsapp_financeiro: negotiationData?.whatsapp_financeiro || '',

                // PJ: documento societário base
                contrato_social_data_versao: negotiationData?.contrato_social_data_versao || '',

                // Plano de Benefícios (simulação escolhida pelo usuário no modal do termo)
                numero_simulacao: simulacaoAnexada.numero_simulacao || '',
                data_simulacao: simulacaoAnexada.data_criacao || '',
                link_simulacao: simulacaoAnexada.url_pdf || '',

                // Local de assinatura ({{cidade e UF}} / {{CIDADE_UF}}) — cidade do endereço do contratante (Lemit); fallback: 1ª UC
                cidade_uf: enderecoTitular
                    ? `${enderecoTitular.cidade}/${enderecoTitular.estado}`
                    : `${primeiraUC.cidade || ''}/${primeiraUC.estado || ''}`,

                // Signatários (Configurações → Assinaturas do Termo)
                testemunha1: signatarios.testemunha1,
                testemunha2: signatarios.testemunha2,
                procurador: signatarios.procurador
            };

            // 3. Mapeia o array de UCs selecionadas (Máximo 60)
            const ucsReais = selectedUCsList.length > 0 ? selectedUCsList : ucs;
            // ANEXO III — colunas: N° UC | Nº Medidor | Endereço | Cidade | Média
            ucsReais.forEach((uc, index) => {
                const num = index + 1;
                if (num <= 60) {
                    const prefix = num < 10 ? `0${num}` : `${num}`;
                    payload[`uc${prefix}`] = uc.numero_uc || '';
                    payload[`medidor${prefix}`] = uc.num_medidor || '';
                    payload[`endereco${prefix}`] = [uc.logradouro, uc.numero].filter(Boolean).join(', ');
                    payload[`cidade${prefix}`] = uc.cidade || '';
                    payload[`media${prefix}`] = uc.consumo_medio_kwh ?? '';
                    // #6367: modelo de cobrança da UC, para o termo poder citar a cláusula do boleto unificado
                    payload[`modelo_cobranca${prefix}`] = cobranca?.modelos[uc.id] ?? (uc.empresa_parceira_id ? 'unificado' : '');
                }
            });
            payload.boleto_unificado_algum = ucsReais.some(uc => cobranca?.modelos[uc.id] === 'unificado');
            payload.aceite_boleto_unificado_pela_assinatura = !!cobranca?.aceitePelaAssinatura;

            // 4. Dispara o webhook
            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // 1. Tenta parsear a resposta do n8n (pode ser vazia)
                let n8nResponse: any = null;
                const responseText = await response.text();
                if (responseText) {
                    try {
                        n8nResponse = JSON.parse(responseText);
                    } catch {
                        console.warn('Resposta do webhook não é JSON:', responseText);
                    }
                }

                // 1.5 RESPOSTA SEM DOCUMENTO = FALHA, e precisa aparecer como falha.
                //
                // O n8n responde pelo nó `Respond to Webhook`, que só roda quando o fluxo
                // chega até ele. Falhando antes — foi o que houve em 25/09/2026 às 12h39,
                // com a Autentique recusando por excesso de tentativas ("Too Many
                // Attempts") no nó `Prepara o doc para assinatura (PF)` — a chamada volta
                // sem `numero_termo`. Até aqui o código seguia adiante: marcava a
                // negociação com a tag 'Termo Gerado', gravava o log "enviou termo de
                // adesão" e mostrava "Termo gerado com sucesso!", sem termo nenhum no
                // histórico e sem nada chegar ao cliente. A Júlia só descobriu porque o
                // cliente não recebeu (negociação 7547).
                //
                // Mesma regra do `liberarAssinaturasTermo`: com n8n, o que vale é a
                // confirmação explícita, nunca o status HTTP.
                if (!n8nResponse?.numero_termo) {
                    console.error('Termo: resposta do n8n sem documento', { status: response.status, corpo: responseText });
                    throw new Error(
                        'A automação do termo respondeu sem o documento — o termo NÃO foi criado e o cliente não recebeu nada. '
                        + 'Isso costuma ser recusa temporária da Autentique por excesso de tentativas: espere alguns minutos e gere de novo. '
                        + 'Se repetir, chame a Tecnologia com o número da negociação.',
                    );
                }

                // 2. Busca os dados atuais da negociação para não sobrescrever o histórico
                const { data: currentNeg } = await supabase
                    .from('negociacoes')
                    .select('tags, url_termo')
                    .eq('id', negotiation.id)
                    .single();

                const currentTags = currentNeg?.tags || [];
                const currentTerms = currentNeg?.url_termo || [];

                // 3. Monta o update base
                const hasTag = currentTags.includes('Termo Gerado');
                const updatedTags = hasTag ? currentTags : [...currentTags, 'Termo Gerado'];
                const updatePayload: any = { tags: updatedTags };

                // 4. Documento confirmado (a guarda acima garante): entra no histórico.
                {
                    const novoTermo = {
                        url_pdf: n8nResponse.url_pdf,
                        data_criacao: new Date().toISOString(),
                        tipo_cliente: n8nResponse.tipo_cliente,
                        numero_termo: `DOC-${n8nResponse.numero_termo.substring(0, 6).toUpperCase()}`,
                        // ID completo do documento no Autentique — necessário para adicionar
                        // procurador/testemunhas ao doc após a aprovação da análise cadastral
                        id_autentique: n8nResponse.id_autentique || n8nResponse.numero_termo || '',
                        // Signatários internos que assinarão após a aprovação da análise cadastral
                        signatarios: {
                            procurador: signatarios.procurador,
                            testemunha1: signatarios.testemunha1,
                            testemunha2: signatarios.testemunha2
                        },
                        // Simulação anexada ao Quadro Resumo — exibida na Identificação Geral (Gestão)
                        numero_simulacao: simulacaoAnexada.numero_simulacao || '',
                        link_simulacao: simulacaoAnexada.url_pdf || '',
                        data_simulacao: simulacaoAnexada.data_criacao || ''
                    };
                    updatePayload.url_termo = [...currentTerms, novoTermo];
                }

                // 5. Atualiza o banco de dados
                const { error: updateError } = await supabase
                    .from('negociacoes')
                    .update(updatePayload)
                    .eq('id', negotiation.id);

                if (updateError) throw updateError;

                // #6367: grava o modelo de cobrança em cada UC do termo, só depois do termo gerado.
                // A assinatura vira aceite pelo gatilho do banco (migration 20261021150000).
                const modelosGravados: Record<string, string> = {};
                if (cobranca) {
                    const agora = new Date().toISOString();
                    const porNome = user?.user_metadata?.full_name || user?.email || 'Sistema';
                    for (const uc of ucsReais) {
                        const modelo = cobranca.modelos[uc.id];
                        if (!modelo) continue;
                        const { data: gravada, error: ucError } = await supabase
                            .from('unidades_consumidoras')
                            .update({
                                modelo_cobranca: modelo,
                                modelo_cobranca_aceite_assinatura: modelo === 'unificado' && cobranca.aceitePelaAssinatura,
                                modelo_cobranca_em: agora,
                                modelo_cobranca_por: porNome,
                            })
                            .eq('id', uc.id)
                            .select('id');
                        if (ucError || !gravada?.length) {
                            addToast(`O termo foi gerado, mas não consegui gravar o modelo de cobrança da UC ${uc.numero_uc}. Avise o financeiro.`, 'warning', 8000);
                            continue;
                        }
                        modelosGravados[uc.numero_uc || String(uc.id)] = modelo;
                    }
                }

                await logNegotiationAction(negotiation.id, user?.id, 'enviou termo de adesão no n8n', {
                    modelo_cobranca: modelosGravados,
                    aceite_boleto_unificado_pela_assinatura: !!cobranca?.aceitePelaAssinatura,
                    tipo_cliente: payload.tipo_cliente,
                    endereco_fonte: enderecoTitular
                        ? (enderecoTitular.fonte === 'uc' ? 'uc_escolhida' : `lemit (${enderecoTitular.fonte})`)
                        : 'primeira_uc',
                    simulacao_anexada: simulacaoAnexada.numero_simulacao || simulacaoAnexada.data_criacao || '',
                });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'gerar_termo_adesao', origem: 'acao' });
                addToast('Termo gerado com sucesso!', 'success');

                // O onUpdate() vai recarregar os dados na tela e o modal vai finalmente mostrar a linha!
                onUpdate();
            } else {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

        } catch (error: any) {
            console.error('Erro ao gerar termo:', error);
            // 12s: a mensagem da falha sem documento diz o que fazer e não cabe no tempo padrão.
            addToast('Erro ao gerar termo: ' + (error.message || 'Erro desconhecido'), 'error', 12000);
        } finally {
            setLoadingAction(null);
        }
    };

    const handleRevertToAnalyse = async () => {
        setLoadingAction('revert_analise');
        try {
            // Fetch the "Funil de Vendas" pipeline to move the negotiation back
            const { data: funilPipeline } = await supabase
                .from('pipelines')
                .select('id')
                .eq('name', 'Funil de Vendas')
                .single();

            const updatePayload: any = { status: 'termo_assinado' };

            // Move back to Funil de Vendas → "Termo de adesão" so it's visible in the CRM
            if (funilPipeline) {
                updatePayload.pipeline_id = funilPipeline.id;
                updatePayload.stage = 'Termo de adesão';
            }

            const { error } = await supabase
                .from('negociacoes')
                .update(updatePayload)
                .eq('id', negotiation.id);

            if (error) throw error;

            await logNegotiationAction(negotiation.id, user?.id, 'reenviou a negociação para validação de dados', {});
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'reenviar_analise_cadastral', origem: 'acao' });

            addToast('Negociação reenviada para análise com sucesso!', 'success');
            onUpdate();
        } catch (error: any) {
            console.error('Erro ao reenviar para análise:', error);
            addToast('Erro ao reenviar para análise: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingAction(null);
        }
    };

    /**
     * Mesma decisão do Passo 4 da Análise Cadastral ("Há necessidade de trocar a
     * titularidade da UC?"), só que respondida direto na tela de negociação do
     * CRM — grava nas duas tabelas pra manter a analista e o vendedor sempre
     * vendo o mesmo estado, não importa por onde foi respondido. Só pode
     * desmarcar enquanto ainda não gerou o documento (pendente_envio); depois
     * disso o botão de gerar já cobre o resto do fluxo.
     */
    const handleToggleNecessitaTrocaTitularidade = async (necessita: boolean) => {
        setLoadingAction('toggle_troca_titularidade');
        try {
            const { error: analiseError } = await supabase
                .from('analise_cadastral')
                .upsert({ negociacao_id: negotiation.id, necessita_troca_titularidade: necessita }, { onConflict: 'negociacao_id' });
            if (analiseError) throw analiseError;

            const { error: negError } = await supabase
                .from('negociacoes')
                .update({ troca_titularidade_status: necessita ? 'pendente_envio' : null })
                .eq('id', negotiation.id);
            if (negError) throw negError;

            await logNegotiationAction(
                negotiation.id,
                user?.id,
                necessita ? 'sinalizou necessidade de troca de titularidade da UC' : 'desmarcou a necessidade de troca de titularidade da UC'
            );
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'marcar_troca_titularidade', origem: 'acao' });

            onUpdate();
        } catch (error: any) {
            console.error('Erro ao atualizar necessidade de troca de titularidade:', error);
            addToast('Erro ao atualizar: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700/50 p-5 shadow-sm space-y-6">

            {/* Responsible Header - Avatar placeholder */}
            <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/20 text-primary flex items-center justify-center text-2xl font-bold mb-3">
                    {negotiation.tipo_cliente === 'PF' ? <House size={32} /> :
                        negotiation.tipo_cliente === 'PJ' ? <Building2 size={32} /> :
                            <User size={32} />}
                </div>
                <h3 className="font-bold text-gray-800 dark:text-slate-100 text-center">{negotiation.cliente_nome}</h3>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Criado em {new Date(negotiation.created_at).toLocaleDateString()}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 border-b border-gray-100 dark:border-slate-700/50 pb-6 mb-2">
                {!simulationGenerated ? (
                    <button
                        onClick={handleSimulateEconomy}
                        className="w-full py-2.5 bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20 dark:border-primary/30"
                    >
                        <Calculator size={16} />
                        Simular economia
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={handleSimulateEconomy}
                            className="flex-1 py-2.5 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-green-100 dark:border-green-800/50"
                        >
                            <Calculator size={16} />
                            Nova simulação
                        </button>
                        <button
                            onClick={() => setShowHistoryModal(true)}
                            className="flex-1 py-2.5 bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20 dark:border-primary/30 animate-fade-in"
                        >
                            <Eye size={16} />
                            Ver simulações
                        </button>
                    </div>
                )}

                {negotiation.desconto_status === 'pendente' ? (
                    <button
                        disabled
                        className="w-full py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-gray-200 dark:border-slate-700 opacity-80 cursor-not-allowed"
                        title="Aguarde a aprovação do limite de descontos pelo seu gerente."
                    >
                        <FileText size={16} />
                        Gerar Termo (Aprovação Pendente)
                    </button>
                ) : negotiation.desconto_status === 'rejeitado' ? (
                    <button
                        disabled
                        className="w-full py-2.5 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-red-200 dark:border-red-800/50 opacity-80 cursor-not-allowed"
                        title="O desconto foi rejeitado. Altere os valores do plano e solicite novamente."
                    >
                        <FileText size={16} />
                        Desconto Rejeitado — Altere o Plano
                    </button>
                ) : !termGenerated ? (
                    <button
                        onClick={() => setIsTermModalOpen(true)}
                        disabled={loadingAction === 'generate_contract'}
                        className={`w-full py-2.5 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 text-warning rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-orange-100 dark:border-orange-800/50 ${loadingAction === 'generate_contract' ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {loadingAction === 'generate_contract' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                        Gerar termo de adesão
                    </button>
                ) : (
                    <>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    if (negotiation.status === 'termo_assinado') {
                                        setShowSignedTermWarning(true);
                                    } else {
                                        setIsTermModalOpen(true);
                                    }
                                }}
                                className="flex-1 py-2.5 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 text-warning rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-orange-100 dark:border-orange-800/50"
                            >
                                <FilePlus2 size={16} />
                                Novo termo
                            </button>
                            <button
                                onClick={() => setShowTermHistoryModal(true)}
                                className="flex-1 py-2.5 bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20 dark:border-primary/30 animate-fade-in"
                            >
                                <Eye size={16} />
                                Ver termos
                            </button>
                        </div>
                    </>
                )}

                {(negotiation.status === 'termo_analise' || negotiation.status === 'termo_reprovado') && !negociacaoEncerrada && (
                    <button
                        onClick={handleRevertToAnalyse}
                        disabled={loadingAction === 'revert_analise'}
                        className={`w-full py-2.5 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-blue-100 dark:border-blue-800/50 ${loadingAction === 'revert_analise' ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {loadingAction === 'revert_analise' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                        Reenviar para validação de dados
                    </button>
                )}

                {/* Avalista é caminho de recuperação de CRÉDITO reprovado, não de
                    qualquer termo_reprovado. O Wizard também carimba termo_reprovado
                    quando a verificação telefônica falha (nao_confirmou / numero_errado
                    / sem_resposta) mantendo decisao_final = 'Aprovado' — nesses casos o
                    trigger avalista_exige_reprovacao recusa o INSERT. Mostrar o painel
                    ali fazia o usuário preencher o modal inteiro para levar o erro do
                    banco na cara. Mesmo gate do TermosReprovadosModal. */}
                {negotiation.status === 'termo_reprovado' && (
                    creditoReprovado ? (
                        <>
                            {!negociacaoEncerrada && <AvalistaRecoveryPanel negociacao={negotiation} onUpdate={onUpdate} />}
                            <DeclaracaoNaoAssociacaoPanel
                                negociacao={negotiation}
                                ucs={ucs}
                                declaracoes={declaracoesNaoAssociacao}
                                onChange={() => { recarregarDeclaracoes(); onUpdate(); }}
                            />
                        </>
                    ) : (
                        <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex items-start gap-2">
                            <ShieldAlert size={13} className="text-slate-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-600 dark:text-slate-300">
                                <strong>Avalista indisponível.</strong>{' '}
                                {decisaoFinalAnalise
                                    ? `A validação de dados consta como "${decisaoFinalAnalise}" — o termo foi reprovado por outro motivo (ex.: verificação telefônica), e o avalista só resolve reprovação de crédito.`
                                    : 'Esta negociação ainda não tem validação de dados concluída com decisão "Reprovado".'}
                            </p>
                        </div>
                    )
                )}

                {(!negotiation.troca_titularidade_status || negotiation.troca_titularidade_status === 'pendente_envio') && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer px-1 py-1">
                        <input
                            type="checkbox"
                            checked={negotiation.troca_titularidade_status === 'pendente_envio'}
                            onChange={(e) => handleToggleNecessitaTrocaTitularidade(e.target.checked)}
                            disabled={loadingAction === 'toggle_troca_titularidade'}
                            className="rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary/40"
                        />
                        Necessita troca de titularidade da UC
                    </label>
                )}

                {negotiation.troca_titularidade_status === 'pendente_envio' && (
                    <button
                        onClick={() => abrirTrocaTitularidade('enviar')}
                        className="w-full py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20"
                    >
                        <Repeat size={16} />
                        Enviar Documento de Troca de Titularidade
                    </button>
                )}
                {negotiation.troca_titularidade_status === 'documento_enviado' && (
                    <>
                        <div
                            className="w-full py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-gray-200 dark:border-slate-700"
                            title="Aguardando o cliente assinar a Declaração de Troca de Titularidade no Autentique."
                        >
                            <Repeat size={16} />
                            Aguardando Assinatura do Cliente
                        </div>
                        {ucsSemTroca > 0 && botaoTrocaOutraUc}
                        <button
                            onClick={() => abrirTrocaTitularidade('enviar')}
                            className="w-full py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20"
                            title="Gera um novo documento e um novo link de assinatura — use se o cliente não recebeu, perdeu o link, ou algum dado precisa ser corrigido."
                        >
                            <RefreshCw size={16} />
                            Reenviar Documento de Troca de Titularidade
                        </button>
                    </>
                )}
                {negotiation.troca_titularidade_status === 'aguardando_reanalise' && (
                    <>
                        <div
                            className="w-full py-2.5 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-teal-200 dark:border-teal-800/50"
                            title="Declaração de Troca de Titularidade assinada. Veja o link/PDF no feed 'Tudo'."
                        >
                            <FileCheck2 size={16} />
                            {ucsComTroca
                                ? `Titularidade assinada: ${ucsTrocaAssinada} de ${ucs.length} UCs`
                                : 'Troca de Titularidade Assinada'}
                        </div>
                        {ucsSemTroca > 0 && botaoTrocaOutraUc}
                        <button
                            onClick={() => setShowSignedTrocaTitularidadeWarning(true)}
                            className="w-full py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border border-primary/20"
                            title="Use se a distribuidora recusou o documento assinado (ex.: endereço sem complemento) e for preciso corrigir e enviar de novo."
                        >
                            <RefreshCw size={16} />
                            Reenviar (corrigir erro)
                        </button>
                    </>
                )}
            </div>

            {/* Form Fields */}
            <div className="space-y-4">

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Funil</label>
                    <select
                        value={formData.pipeline_id}
                        onChange={(e) => handlePipelineChange(e.target.value)}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100 font-medium"
                    >
                        {pipelines.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Etapa</label>
                    <select
                        value={formData.stage}
                        onChange={(e) => {
                            setFormData({ ...formData, stage: e.target.value as any });
                            handleBlur('stage', e.target.value);
                        }}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    >
                        {pipelineStages
                            .filter(stage => stage.pipeline_id === formData.pipeline_id)
                            .length > 0 ? (
                            pipelineStages
                                .filter(stage => stage.pipeline_id === formData.pipeline_id)
                                .map(stage => (
                                    <option key={stage.id} value={stage.name}>{nomeEtapaNaTela(stage.name)}</option>
                                ))
                        ) : (
                            <option value={formData.stage}>{nomeEtapaNaTela(formData.stage)} (Arquivado)</option>
                        )
                        }
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Média de Consumo (kWh)</label>
                    <div className="relative">
                        <input
                            type="number"
                            value={ucs.reduce((acc, uc) => acc + (uc.consumo_medio_kwh || 0), 0)}
                            disabled
                            className="w-full border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-slate-400 outline-none cursor-not-allowed"
                        />
                        <Zap size={14} className="absolute right-3 top-2.5 text-gray-400 dark:text-slate-500" />
                    </div>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Soma automática das UCs vinculadas</p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Início da negociação</label>
                    <input
                        type="date"
                        disabled
                        value={formData.created_at}
                        className="w-full border border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-slate-400 outline-none"
                    />
                </div>

                {/* Previsão de fechamento — oculta provisoriamente a pedido do usuário. Lógica (formData/handleBlur/getDelayText) mantida para reativação futura. */}

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Origem</label>
                    <select
                        value={formData.origem}
                        onChange={(e) => {
                            setFormData({ ...formData, origem: e.target.value });
                            handleBlur('origem', e.target.value);
                        }}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    >
                        {/* Origem gravada fora da lista (link do parceiro, importação antiga): aparece
                            como está, em vez de a tela mostrar a primeira opção no lugar. */}
                        {formData.origem && !(ORIGENS_NEGOCIACAO as readonly string[]).includes(formData.origem) && (
                            <option value={formData.origem}>{formData.origem}</option>
                        )}
                        {ORIGENS_NEGOCIACAO.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Plataforma/Canal</label>
                    <input
                        type="text"
                        value={formData.plataforma}
                        onChange={(e) => setFormData({ ...formData, plataforma: e.target.value })}
                        onBlur={(e) => handleBlur('plataforma', e.target.value)}
                        placeholder="Ex: Facebook, Instagram, Google"
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Campanha</label>
                    <input
                        type="text"
                        value={formData.campanha}
                        onChange={(e) => setFormData({ ...formData, campanha: e.target.value })}
                        onBlur={(e) => handleBlur('campanha', e.target.value)}
                        placeholder="Nome da campanha"
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Anúncio</label>
                    <input
                        type="text"
                        value={formData.anuncio}
                        onChange={(e) => setFormData({ ...formData, anuncio: e.target.value })}
                        onBlur={(e) => handleBlur('anuncio', e.target.value)}
                        placeholder="Nome do anúncio"
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Formulário</label>
                    <input
                        type="text"
                        value={formData.formulario}
                        onChange={(e) => setFormData({ ...formData, formulario: e.target.value })}
                        onBlur={(e) => handleBlur('formulario', e.target.value)}
                        placeholder="Nome do formulário"
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Reside em Goiás?</label>
                    <select
                        value={formData.reside_goias}
                        onChange={(e) => {
                            const novoValor = e.target.value;
                            setFormData({ ...formData, reside_goias: novoValor });
                            handleBlur('reside_goias', novoValor === 'sim' ? true : novoValor === 'nao' ? false : null);
                        }}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100"
                    >
                        <option value="">Não informado</option>
                        <option value="sim">Sim</option>
                        <option value="nao">Não</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase mb-1">Responsável</label>
                    <div
                        onClick={() => podeTransferir && setShowTransferModal(true)}
                        className={`flex items-center gap-2 p-2 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg text-gray-500 dark:text-slate-400 transition-colors
                            ${podeTransferir ? 'hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800 cursor-pointer' : 'cursor-default opacity-80'}
                        `}
                    >
                        {negotiation.responsavel?.avatar_url ? (
                            <img src={negotiation.responsavel.avatar_url} alt="Responsável" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                            <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold uppercase">
                                {negotiation.responsavel?.full_name?.charAt(0) || '?'}
                            </div>
                        )}
                        <span className="text-sm truncate flex-1">{negotiation.responsavel?.full_name || 'Sem responsável'}</span>
                        {podeTransferir && <span className="text-[10px] text-primary font-bold">Alterar</span>}
                    </div>
                </div>

                {/* Tags — ocultas provisoriamente a pedido do usuário. Tags continuam sendo gravadas em negotiation.tags (ex.: auto-tag "Termo Gerado") mesmo sem esta UI. */}

                <div className="pt-8 mt-auto">
                    <button
                        onClick={handleDelete}
                        className="w-full py-2 border border-red-100 dark:border-red-900/50 text-red-500 dark:text-red-400 rounded-lg text-sm hover:bg-red-50 dark:hover:bg-red-900/40 dark:bg-red-900/20 transition-colors"
                    >
                        Excluir negociação
                    </button>
                </div>

            </div>

            <TransferUserModal
                isOpen={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onSelect={handleTransfer}
                currentResponsibleId={negotiation.responsavel_id}
                filtrarDestino={transferenciaSoNaEquipe ? (p => String(p.id) === String(user?.id) || ehDaEquipeSupervisionada(p)) : undefined}
            />

            <TrocaTitularidadeModal
                isOpen={showTrocaTitularidadeModal}
                onClose={() => {
                    setShowTrocaTitularidadeModal(false);
                    setReleituraTroca(n => n + 1);
                    onUpdate();
                }}
                negotiation={negotiation}
                ucs={ucs}
                onGerado={onUpdate}
                modo={modoTrocaTitularidade}
            />

            <SimulationModal
                isOpen={isSimulationModalOpen}
                onClose={() => setIsSimulationModalOpen(false)}
                negotiationId={negotiation.id}
                clientName={negotiation.cliente_nome}
                ucs={ucs}
                onUpdate={() => {
                    onUpdate();
                    setShowHistoryModal(true);
                }}
            />

            <SimulationHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                simulations={negotiation.url_simulacao}
            />

            {/* Term Generation Modal (reuses SimulationModal for UC selection) */}
            <SimulationModal
                isOpen={isTermModalOpen}
                onClose={() => setIsTermModalOpen(false)}
                negotiationId={negotiation.id}
                clientName={negotiation.cliente_nome}
                ucs={ucs}
                mode="term"
                cpfCnpj={negotiation.cpf_cnpj}
                simulations={negotiation.url_simulacao}
                onSubmit={handleGenerateContract}
                onUpdate={() => {
                    setIsTermModalOpen(false);
                    addToast('Aguarde 15 segundos antes de clicar em "Ver termos" para o documento aparecer no histórico.', 'info');
                    onUpdate();
                }}
            />

            <TermHistoryModal
                isOpen={showTermHistoryModal}
                onClose={() => setShowTermHistoryModal(false)}
                terms={negotiation.url_termo}
                clientLink={negotiation.termo_link_do_cliente}
                negociacaoId={negotiation.id}
                onSynced={onUpdate}
            />

            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={executeDelete}
                title="Excluir Negociação"
                message={`Tem certeza que deseja excluir a negociação "${negotiation.cliente_nome}"? Todas as UCs, tarefas, notas e logs associados serão removidos permanentemente.`}
                confirmLabel="Sim, excluir"
                cancelLabel="Cancelar"
                variant="danger"
            />

            <ConfirmModal
                isOpen={showSignedTermWarning}
                onClose={() => setShowSignedTermWarning(false)}
                onConfirm={() => {
                    setShowSignedTermWarning(false);
                    setIsTermModalOpen(true);
                }}
                title="Cliente já assinou o termo"
                message={`Este cliente já assinou o termo de adesão${negotiation.data_assinatura_termo ? ` em ${new Date(negotiation.data_assinatura_termo).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}. Enviar um novo termo irá invalidar o anterior e o cliente precisará assinar novamente. Tem certeza que deseja continuar?`}
                confirmLabel="Sim, enviar novo termo"
                cancelLabel="Cancelar"
                variant="warning"
            />

            <ConfirmModal
                isOpen={showSignedTrocaTitularidadeWarning}
                onClose={() => setShowSignedTrocaTitularidadeWarning(false)}
                onConfirm={() => {
                    setShowSignedTrocaTitularidadeWarning(false);
                    abrirTrocaTitularidade('corrigir');
                }}
                title="Cliente já assinou a Declaração de Troca de Titularidade"
                message="Use esta opção apenas se a distribuidora recusou o documento assinado (ex.: endereço sem complemento) e for necessário corrigir e enviar de novo. Isso gera um novo documento/link e o cliente precisará assinar novamente — o documento anterior fica registrado no histórico. Tem certeza que deseja continuar?"
                confirmLabel="Sim, reenviar com correção"
                cancelLabel="Cancelar"
                variant="warning"
            />
        </div>
    );
};

export default DetailLeftColumn;
